import styled from '@emotion/styled';
import { motion } from 'motion/react';
import type { ReactNode } from 'react';

import type { ActionIconVariant } from '../actionIcon/ActionIcon';
import { ActionIcon } from '../actionIcon/ActionIcon';
import { FrontButton } from '../button/FrontButton';
import { Flex } from '../flex/Flex';
import {
  IconCheckCircleSolid,
  IconErrorSolid,
  IconInformationCircleSolid,
  IconXmark,
} from '../icons';
import { breakpoints } from '../theme/media';
import { themeStyles, themeVars } from '../theme/theme';
import type { NotificationDef, NotificationType } from './notificationsStore';
import { Notifications } from './notificationsStore';
import { useAutoCloseNotification } from './useAutoCloseNotification';

interface NotificationProps {
  notification: NotificationDef;
}

const iconByType: Record<NotificationType, ReactNode> = {
  error: <IconErrorSolid size={20} />,
  info: <IconInformationCircleSolid size={20} />,
  success: <IconCheckCircleSolid size={20} />,
  notice: <IconInformationCircleSolid size={20} />,
};

const closeButtonByType: Record<NotificationType, ActionIconVariant> = {
  error: 'error',
  info: 'info',
  success: 'success',
  notice: 'notice',
};

export const Notification = ({ notification }: NotificationProps) => {
  const {
    id,
    message,
    title,
    type,
    hightContrast = true,
    icon,
    autoClose = true,
    closeDuration = 5000,
  } = notification;

  const { handleMouseEnter, handleMouseLeave } = useAutoCloseNotification({
    id,
    autoClose,
    closeDuration,
  });

  return (
    <Root
      type={type}
      data-type={type}
      data-hight-contrast={hightContrast}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      initial={{
        opacity: 0,
        y: 50,
      }}
      animate={{
        opacity: 1,
        y: 0,
        scale: 1,
      }}
      exit={{
        opacity: 0,
        y: -50,
      }}
    >
      <IconContainer>{icon ?? iconByType[type]}</IconContainer>
      <Content>
        <Flex justify="space-between">
          <Title data-title>{title}</Title>
          {hightContrast ? (
            <FrontButton
              variant="ghost"
              appearance="onDark"
              size="xs"
              isIcon
              onClick={() => Notifications.hide(id)}
            >
              <IconXmark size={16} />
            </FrontButton>
          ) : (
            <ActionIcon
              variant={closeButtonByType[type]}
              size="xs"
              onClick={() => Notifications.hide(id)}
            >
              <IconXmark size={16} />
            </ActionIcon>
          )}
        </Flex>
        <Message data-message>{message}</Message>
      </Content>
    </Root>
  );
};

const Root = styled(motion.div)<{ type: NotificationType }>`
  padding: 12px;
  min-width: 292px;
  max-width: 368px;
  display: flex;
  align-items: flex-start;
  position: relative;
  border-radius: 8px;
  gap: 8px;

  & + & {
    margin-top: 8px;
  }

  ${breakpoints.before.laptop} {
    min-width: 0;
    max-width: 100%;
    width: 100%;
  }

  &[data-type='info'] {
    background-color: ${themeVars.colors.info100};
    border: 1px solid ${themeVars.colors.info200};
    color: ${themeVars.colors.info600};

    [data-title] {
      color: ${themeVars.colors.info600};
    }

    [data-message] {
      ${themeStyles.text.telka.body.md};
      color: #3a4145;
    }
  }

  &[data-type='success'] {
    background-color: ${themeVars.colors.success100};
    border: 1px solid ${themeVars.colors.success200};
    color: ${themeVars.colors.success600};

    [data-title] {
      color: ${themeVars.colors.success600};
    }
  }

  &[data-type='notice'] {
    background-color: ${themeVars.colors.notice100};
    border: 1px solid ${themeVars.colors.notice200};
    color: ${themeVars.colors.notice600};

    [data-title] {
      color: ${themeVars.colors.notice600};
    }
  }

  &[data-type='error'] {
    background-color: ${themeVars.colors.error100};
    border: 1px solid ${themeVars.colors.error200};
    color: ${themeVars.colors.error600};

    [data-title] {
      color: ${themeVars.colors.error600};
    }
  }

  &[data-hight-contrast='true'] {
    border: none;
    background: ${themeVars.colors.titan900};

    [data-title] {
      color: ${themeVars.colors.titan25};
    }
    [data-message] {
      color: ${themeVars.colors.titan300};
    }

    &[data-type='info'] {
      border-left: 4px solid ${themeVars.colors.info500};
      color: ${themeVars.colors.info500};
    }

    &[data-type='success'] {
      border-left: 4px solid ${themeVars.colors.success400};
      color: ${themeVars.colors.success400};
    }

    &[data-type='notice'] {
      border-left: 4px solid ${themeVars.colors.notice300};
      color: ${themeVars.colors.notice300};
    }

    &[data-type='error'] {
      border-left: 4px solid ${themeVars.colors.error500};
      color: ${themeVars.colors.error500};
    }
  }
`;

const Message = styled.div`
  ${themeStyles.text.telka.body.md};
  color: #3a4145;
`;

const IconContainer = styled.div`
  padding-top: 2px;
`;

const Content = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const Title = styled.div`
  ${themeStyles.text.telkaEx.body.md};
`;
